import React, { useEffect } from "react";
import { SimpleTreeView } from "@mui/x-tree-view";
import Typography from "@mui/material/Typography";
import { CustomTreeItem } from "./CustomTreeItem";
import Divider from "@mui/material/Divider";
import SearchBar from "../../../../../components/ui/search/SearchBar";
import { useTheme } from "@mui/material/styles";
import { useLocation, useNavigate } from "react-router-dom";
import WarehouseIcon from "@mui/icons-material/Warehouse";
import { useTreeView } from "./hooks/useTreeView";
import Business from "@mui/icons-material/Business";
import ViewModule from "@mui/icons-material/ViewModule";
import ElectricMeter from "@mui/icons-material/ElectricMeter";
import { useAppDispatch, useAppSelector } from "../../../../../hooks/storeHooks";
import {
  selectSearch,
  selectSelectedTreeItem,
  setExpandedItemByDepth,
  setSearch,
  setSelectedTreeItemByResourceId,
} from "../../../layoutSlice";
import type { TreeItemDataOut, TreeViewProps } from "../../../services/dashboardTypes";

export default function CustomersTreeView({ data }: TreeViewProps) {
  const location = useLocation();
  const theme = useTheme();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const selectedTreeItem = useAppSelector(selectSelectedTreeItem);

  const search = useAppSelector(selectSearch);
  const { expandedItemsByDepth, handleItemClick, handleIconClick } = useTreeView();

  const determineIcon = React.useCallback((depth: number) => {
    switch (depth) {
      case 0:
        return Business;
      case 1:
        return WarehouseIcon;
      case 2:
        return ViewModule;
      case 3:
        return ElectricMeter;
      default:
        return WarehouseIcon;
    }
  }, []);

  const renderTreeItems = React.useCallback(
    (items: TreeItemDataOut[], depth = 0): React.ReactNode =>
      items?.map((item) => (
        <CustomTreeItem
          key={item.id}
          itemId={item.id.toString()}
          label={item.name}
          labelStatus={item?.status}
          labelIcon={determineIcon(depth)}
          onClick={(event) => {
            handleItemClick(event, item.path, item.id.toString());
          }}
          onIconClick={(event) => handleIconClick(event, item.path, item.id.toString(), item.name || "", depth)}
        >
          {item.children && renderTreeItems(item.children, depth + 1)}
        </CustomTreeItem>
      )),
    [determineIcon, handleIconClick, handleItemClick],
  );

  const memoizedTreeItems = React.useMemo(() => renderTreeItems(data), [data, renderTreeItems]);

  useEffect(() => {
    const isDashboardRoute = location.pathname === "/dashboard";

    if (data && data.length > 0 && isDashboardRoute) {
      const firstItem = data[0];

      if (firstItem.children && firstItem.children.length === 1) {
        const singleChild = firstItem.children[0];
        dispatch(setSelectedTreeItemByResourceId(singleChild.id.toString()));
        navigate(singleChild.path);
        dispatch(setExpandedItemByDepth({ depth: 1, itemId: singleChild.id }));
      } else {
        dispatch(setSelectedTreeItemByResourceId(firstItem.id.toString()));
        navigate(firstItem.path);
        dispatch(setExpandedItemByDepth({ depth: 0, itemId: firstItem.id }));
      }
    }
  }, [data, dispatch, location.pathname, navigate]);

  return (
    <>
      <Typography sx={{ fontWeight: "bold", mt: 3, mb: 1, pl: 1.5 }}>Dashboard</Typography>
      <SearchBar
        fullWidth
        sx={{
          px: 1,
          pb: 1,
          pt: 0.5,
          div: { background: theme.palette.background.default },
        }}
        value={search}
        onSearch={(text) => dispatch(setSearch(text))}
      />
      <SimpleTreeView
        aria-label="navigation-tree"
        sx={{
          px: 1.5,
          maxHeight: 450,
          overflow: "auto",
          scrollbarWidth: "thin",
          scrollbarColor: theme.palette.background.default,
        }}
        selectedItems={selectedTreeItem?.id || null}
        expandedItems={Object.values(expandedItemsByDepth)}
      >
        {memoizedTreeItems}
      </SimpleTreeView>
      <Divider sx={{ mt: 1 }} />
    </>
  );
}
